import { atom } from "jotai";
import type { UIMessage } from "@zapier/agent-system";

export type ComposerService = {
  sendMessage: (message: string, additionalData?: Record<string, any>) => void;
  resetChat: () => void;
  setMessages: (
    messages: UIMessage[] | ((prev: UIMessage[]) => UIMessage[]),
  ) => void;
  handleSubmit: (
    event?: { target?: { value?: string } },
    options?: any,
  ) => void;
};

/**
 * This atom stores a reference to the composer service API.
 * Components can import this atom and use it to send messages to the chat interface.
 *
 * To register a ComposerService, you should do:
 * ```
 * const setComposerService = useSetAtom(composerServiceAtom);
 *
 * useEffect(() => {
 *   setComposerService({
 *     sendMessage: (message, data) => { ... },
 *     resetChat: () => { ... },
 *     setMessages: (messages) => { ... },
 *     handleSubmit: (event, options) => { ... }
 *   });
 *
 *   return () => setComposerService(null);
 * }, []);
 */
export const composerServiceAtom = atom<ComposerService | null>(null);

/**
 * Write-only atom that sends a message to the composer.
 * This is convenient for using in other atoms or effects.
 *
 * Example usage:
 * ```
 * const sendMessage = useSetAtom(sendMessageAtom);
 * sendMessage({ message: "Hello", data: { context: "example" } });
 * ```
 *
 * Or in another atom:
 * ```
 * export const myAtom = atom(
 *   null,
 *   (get, set, params) => {
 *     // Do something
 *     set(sendMessageAtom, { message: "Task completed", data: { result: params } });
 *   }
 * );
 * ```
 */
export const sendMessageAtom = atom(
  null,
  (get, set, params: { message: string; data?: Record<string, any> }) => {
    const composerService = get(composerServiceAtom);
    if (composerService) {
      composerService.sendMessage(params.message, params.data);
    } else {
      console.error(
        "Attempted to send message, but composer service is not available:",
        params,
      );
    }
  },
);

/**
 * Write-only atom that resets the composer chat.
 */
export const resetChatAtom = atom(null, (get) => {
  const composerService = get(composerServiceAtom);
  if (composerService) {
    composerService.resetChat();
  } else {
    console.error(
      "Attempted to reset chat, but composer service is not available",
    );
  }
});

/**
 * Write-only atom that sets the messages in the composer.
 * This allows direct manipulation of the message history.
 *
 * Example usage:
 * ```
 * const setMessages = useSetAtom(setMessagesAtom);
 * setMessages([
 *   { role: 'user', content: 'Hello', id: '1' },
 *   { role: 'assistant', content: 'Hi there!', id: '2' }
 * ]);
 * ```
 *
 * Or with a function to update based on previous messages:
 * ```
 * setMessages(prev => [...prev, newMessage]);
 * ```
 */
export const setMessagesAtom = atom(
  null,
  (get, set, messages: UIMessage[] | ((prev: UIMessage[]) => UIMessage[])) => {
    const composerService = get(composerServiceAtom);
    if (composerService) {
      composerService.setMessages(messages);
    } else {
      console.error(
        "Attempted to set messages, but composer service is not available",
        messages,
      );
    }
  },
);

/**
 * Write-only atom that submits the current message thread to the LLM.
 * This is useful when you've just added messages to the thread and want
 * to have the LLM process them.
 *
 * Example usage:
 * ```
 * const handleSubmit = useSetAtom(handleSubmitAtom);
 * handleSubmit({ options: { data: { context: "example" } } });
 * ```
 */
export const handleSubmitAtom = atom(
  null,
  (
    get,
    set,
    params?: { event?: { target?: { value?: string } }; options?: any },
  ) => {
    const composerService = get(composerServiceAtom);
    if (composerService) {
      composerService.handleSubmit(params?.event, params?.options);
    } else {
      console.error(
        "Attempted to submit messages, but composer service is not available",
        params,
      );
    }
  },
);

composerServiceAtom.debugLabel = "composerServiceAtom";
sendMessageAtom.debugLabel = "sendMessageAtom";
resetChatAtom.debugLabel = "resetChatAtom";
setMessagesAtom.debugLabel = "setMessagesAtom";
handleSubmitAtom.debugLabel = "handleSubmitAtom";
